import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { getClientWhatsAppMessages, getClientWhatsAppOptIn, sendWhatsApp } from "api/whatsapp";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";

const RECENT_MESSAGES = { page: 0, size: 50 };

function errorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || fallback;
}

export default function SendWhatsAppDialog({ open, clientId, clientName, onClose }) {
  const [text, setText] = useState("");
  const [optedIn, setOptedIn] = useState(false);
  const [recordOptIn, setRecordOptIn] = useState(false);
  const [messages, setMessages] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const canRead = hasPermission(P.MESSAGING.WHATSAPP_READ);

  useEffect(() => {
    if (!open || !clientId) return;
    setText("");
    setConfirming(false);
    getClientWhatsAppOptIn(clientId)
      .then((res) => {
        const value = Boolean(res?.data?.data);
        setOptedIn(value);
        setRecordOptIn(value);
      })
      .catch(() => {
        setOptedIn(false);
        setRecordOptIn(false);
      });
    if (canRead) {
      getClientWhatsAppMessages(clientId, RECENT_MESSAGES)
        .then((res) => setMessages(res?.data?.data?.content || []))
        .catch(() => setMessages([]));
    }
  }, [open, clientId, canRead]);

  const close = () => {
    if (sending) return;
    setConfirming(false);
    onClose();
  };

  const send = () => {
    setSending(true);
    sendWhatsApp({ clientId, text, recordOptIn: recordOptIn || optedIn })
      .then(() => {
        toast.success("WhatsApp message sent");
        setConfirming(false);
        setText("");
        onClose();
      })
      .catch((error) => toast.error(errorMessage(error, "Could not send the WhatsApp message")))
      .finally(() => setSending(false));
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>WhatsApp {clientName ? `to ${clientName}` : ""}</DialogTitle>
      <DialogContent className="space-y-3">
        {confirming ? (
          <p className="text-sm text-app-text pt-2">
            Send this WhatsApp message to {clientName || "this client"}? It goes out on the
            connected session immediately.
          </p>
        ) : (
          <>
            <TextField
              label="Message"
              value={text}
              onChange={(event) => setText(event.target.value)}
              fullWidth
              multiline
              minRows={3}
              margin="dense"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={recordOptIn || optedIn}
                  disabled={optedIn}
                  onChange={(event) => setRecordOptIn(event.target.checked)}
                />
              }
              label={
                optedIn
                  ? "This client has opted in to WhatsApp"
                  : "Record that this client has opted in to WhatsApp"
              }
            />
            {canRead && messages.length > 0 ? (
              <ul className="max-h-40 overflow-auto text-sm text-app-text-secondary space-y-1">
                {messages.map((message) => (
                  <li key={message.id}>
                    {message.direction === "OUTBOUND" ? "Sent" : "Received"}: {message.body}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={confirming ? () => setConfirming(false) : close} disabled={sending}>
          {confirming ? "Back" : "Cancel"}
        </Button>
        {confirming ? (
          <Button variant="contained" color="primary" onClick={send} disabled={sending}>
            Send
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            disabled={!text.trim() || !(recordOptIn || optedIn)}
            onClick={() => setConfirming(true)}
          >
            Review
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
